import { ctxConsoleLog } from '@citrite/msal-browser';
import { PlatformDependencies } from '@citrite/workspace-ui-platform';
import { platform } from 'Environment/launchResource/constants';
import { getCurrentPlatform } from 'Environment/launchResource/device';
import { getUpnOrAuthAliasForCurrentUser } from 'Environment/msal/clientInfoUtils';
import { container } from 'Workspace/DependencyManagement';
import { TrimmedKeyMap } from 'Workspace/UserContext/trimmedKeyMap';
import { getUpnFromOnPremUserDetails } from 'Workspace/UserContext/upnUtil';

class UpnHandlerForNativeAADFlow {
	private currentUserName: string;
	private userToUpnMap: TrimmedKeyMap<string> = new TrimmedKeyMap<string>();

	public setCurrentUserName(userName: string) {
		this.currentUserName = userName;
	}

	public getCurrentUserName(): string {
		return this.currentUserName;
	}

	public setUserToUpnMapping(userName: string, upn: string) {
		this.userToUpnMap.set(userName, upn);
	}

	// We should address the issue in UserContext later.
	// https://citrix.atlassian.net/browse/WSUI-11818
	// Currently due to release deadline for OnPrem CR2603 and Allianz Cloud release requirement,
	// we are adding this workaround to only rely localstorage in cloud or in
	// mobile clients which fully reload the webview after user logout.
	public shouldReadUpnFromLocalStorage(): boolean {
		// For all cloud stores, WSUI webview is always reloaded when user logs-out.
		if (!IS_ON_PREM) {
			return true;
		}

		const onPremPlatformsWithPersistentWebview = [
			platform.windows,
			platform.mac,
			platform.linux,
		];

		const ret = !onPremPlatformsWithPersistentWebview.includes(getCurrentPlatform());

		ctxConsoleLog('!! UpnHandlerForNativeAADFlow.shouldReadUpnFromLocalStorage =  ', ret);

		return ret;
	}

	public waitForValidUserName(timeoutMs = 4000): Promise<boolean> {
		return new Promise(resolve => {
			const maxAttempts = timeoutMs / 100;
			let attempts = 0;
			const checkUserName = () => {
				if (this.currentUserName?.trim().length > 0) {
					resolve(true);
				} else if (attempts >= maxAttempts) {
					resolve(false);
				} else {
					attempts++;
					setTimeout(checkUserName, 100);
				}
			};
			checkUserName();
		});
	}

	private async getCachedUpn(): Promise<string | undefined> {
		try {
			if (this.shouldReadUpnFromLocalStorage()) {
				return getUpnOrAuthAliasForCurrentUser();
			}

			const isValidUserName = await this.waitForValidUserName();

			if (!isValidUserName) {
				return undefined;
			}

			//Try to get UPN from map first
			const upnFromMap = this.userToUpnMap.get(this.currentUserName);
			return upnFromMap?.trim();
		} catch (e) {
			ctxConsoleLog('Error in getCachedUpn: ', e);
			return undefined;
		}
	}

	public async getUpnForCurrentUser(): Promise<string> {
		try {
			const cachedUpn = await this.getCachedUpn();

			if (cachedUpn) {
				ctxConsoleLog('UPN fetched from cache/localStorage: ', cachedUpn);
				return cachedUpn;
			}

			ctxConsoleLog('No valid UPN in cache/localStorage, fetching from network...');

			// Only if Cached values fail always fetch UPN from network.
			const wsConfig = container
				.resolve(PlatformDependencies.WorkspaceConfiguration)
				.get();
			const getUserDetailsURL = wsConfig?.authManager?.getUserDetailsURL;
			const upnDetails = await getUpnFromOnPremUserDetails(getUserDetailsURL);
			return upnDetails.upn;
		} catch (e) {
			ctxConsoleLog('Error in getUpnForCurrentUser: ', e);
			return '';
		}
	}
}

export const upnHandlerForNativeFlow = new UpnHandlerForNativeAADFlow();
